import { useEffect, useState } from 'react';

import { probePortalExtension } from 'services/portalExtension';

// How long to wait before the last retry. The dominant false-negative cause is the content
// script's `document_idle` injection losing the race with a React effect on a hydrated SPA
// route, so the retries are spaced to outlast page load rather than to outlast the ack.
const RETRY_DELAY_MS = 1000;

// THE answer for the page, not per component. Resolved `true` is cached for the document's
// lifetime — an extension does not uninstall mid-session in any way that matters, and every
// consumer sharing one answer is the point. `false` is deliberately NOT cached: it is the
// unreliable half of the probe, so a later caller gets a fresh look.
let resolvedPresent = false;
// One in-flight detection at a time, shared by every caller that arrives while it runs — four
// independent coin flips per page is the failure mode this hook exists to remove.
let inFlight: Promise<boolean> | null = null;

function waitForLoad(): Promise<void> {
  if (typeof document === 'undefined' || document.readyState === 'complete') return Promise.resolve();
  return new Promise((resolve) => window.addEventListener('load', () => resolve(), { once: true }));
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Probe, and on a negative give the content script the two chances it plausibly needs: once the
// document has finished loading (which is what `document_idle` waits for) and once more a second
// later. Only a negative after all three commits to "absent".
async function detect(): Promise<boolean> {
  if (await probePortalExtension()) return true;

  await waitForLoad();
  if (await probePortalExtension()) return true;

  await delay(RETRY_DELAY_MS);
  return probePortalExtension();
}

/**
 * Is the Olio browser extension present on this browser?
 *
 * Returns `null` until the answer is known — callers must branch on three states, because
 * rendering the M0 (no extension) surface during detection is exactly the silent mode
 * reclassification this hook exists to prevent: M0 and M1 are the same client setting
 * distinguished only by this probe.
 */
export function useExtensionPresence(): { present: boolean | null; isProbing: boolean } {
  const [present, setPresent] = useState<boolean | null>(resolvedPresent ? true : null);

  useEffect(() => {
    if (resolvedPresent) {
      setPresent(true);
      return;
    }

    let canceled = false;
    inFlight ??= detect().finally(() => {
      inFlight = null;
    });
    inFlight.then((found) => {
      if (found) resolvedPresent = true;
      if (!canceled) setPresent(found);
    });

    return () => {
      canceled = true;
    };
  }, []);

  return { present, isProbing: present === null };
}

// Test seam only. The memo is module state on purpose (it must outlive every component that
// asks), which means a test that resolves `true` would otherwise poison the next one.
export function resetExtensionPresence() {
  resolvedPresent = false;
  inFlight = null;
}
